import { useEffect, ReactNode, useRef } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Merchant, MerchantApplication } from '@bevpass/types';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { initializeAuth, user, currentMerchant, setCurrentMerchant } = useAuthStore(state => ({
    initializeAuth: state.initializeAuth,
    user: state.user,
    currentMerchant: state.currentMerchant,
    setCurrentMerchant: state.setCurrentMerchant
  }));
  const initialized = useRef(false);

  useEffect(() => {
    // Only initialize once
    if (initialized.current) return;
    initialized.current = true;
    
    // Initialize auth listener
    const unsubscribe = initializeAuth();
    
    // Cleanup on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []); // Empty dependency array - only run once

  // Auto-select merchant context for merchant users
  useEffect(() => {
    let cancelled = false;

    const autoSelectMerchant = async () => {
      // Only for merchant users who are logged in
      if (!user || !user.role || user.role === 'bevpass_admin') return;
      if (currentMerchant) return;
      
      const isMerchantUser = user.role === 'merchant_owner' || user.role === 'merchant_viewer';
      if (!isMerchantUser) return;

      try {
        // Check for merchantIds first (both pending_setup and approved merchants will have this)
        if (user.merchantIds && user.merchantIds.length > 0) {
          // Load the first merchant
          const merchantId = user.merchantIds[0];
          const merchantDoc = await getDoc(doc(db, 'merchants', merchantId));

          if (merchantDoc.exists()) {
            const merchant = { 
              id: merchantDoc.id, 
              ...merchantDoc.data() 
            } as Merchant;
            if (!cancelled) {
              setCurrentMerchant(merchant);
              console.log('Auto-selected merchant:', merchant.name);
            }
            return; // Exit early if merchant found
          }
        }
        
        // Fallback: Query for merchants owned by this user
        // This handles cases where the custom claims haven't been updated yet
        if (!user.merchantIds || user.merchantIds.length === 0) {
          console.log('No merchantIds in claims, querying by ownerId...');
          const merchantsQuery = query(
            collection(db, 'merchants'),
            where('ownerId', '==', user.uid)
          );
          const merchantSnapshot = await getDocs(merchantsQuery);

          if (!merchantSnapshot.empty) {
            const merchantDoc = merchantSnapshot.docs[0];
            const merchant = {
              id: merchantDoc.id,
              ...merchantDoc.data()
            } as Merchant;
            if (!cancelled) {
              setCurrentMerchant(merchant);
              console.log('Auto-selected merchant by ownerId:', merchant.name);
            }
            return; // Exit early if merchant found
          }
        }

        // Fallback: check if they have an applicationId but no merchant (legacy)
        if (user.applicationId) {
          // Load the application to get merchant info
          const appDoc = await getDoc(doc(db, 'merchantApplications', user.applicationId));
          if (appDoc.exists()) {
            const application = appDoc.data() as MerchantApplication;
            
            // Check if the application has a merchantId
            if (application.merchantId) {
              // Load the actual merchant
              const merchantDoc = await getDoc(doc(db, 'merchants', application.merchantId));
              if (merchantDoc.exists()) {
                const merchant = { 
                  id: merchantDoc.id, 
                  ...merchantDoc.data() 
                } as Merchant;
                if (!cancelled) {
                  setCurrentMerchant(merchant);
                  console.log('Auto-selected merchant from application merchantId:', merchant.name);
                }
              }
            } else {
              // Create a temporary merchant object for display (legacy support)
              const tempMerchant: Merchant = {
                id: appDoc.id,
                name: application.business.name,
                description: application.business.description || '',
                contactInfo: {
                  email: application.business.email,
                  phone: application.business.phone,
                  businessType: application.business.businessType,
                  businessName: application.business.businessName
                },
                address: application.business.address,
                settings: application.settings,
                applicationId: appDoc.id,
                applicationStatus: 'pending_setup',
                createdAt: application.createdAt,
                updatedAt: application.updatedAt
              };
              if (!cancelled) {
                setCurrentMerchant(tempMerchant);
                console.log('Auto-selected temporary merchant from application:', tempMerchant.name);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error auto-selecting merchant:', error);
        // Don't throw - just log the error
      }
    };

    if (user) {
      void autoSelectMerchant();
    }

    return () => {
      cancelled = true;
    };
  }, [user, currentMerchant, setCurrentMerchant]);

  return <>{children}</>;
}
